import React, { useEffect, useState, useCallback } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Receipt, Plus, Trash2, Save, Loader2, Info, Globe } from "lucide-react";
import { fmtMoney } from "./money";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";

// Mirrors platform_settings_routes.py's update_tax_config: `rate < 0 or rate > 100` is a
// 400 today, reached only after the round trip. Each line gets its OWN top-level key
// (`rate_0`, `rate_1`, …) rather than a nested `tax_lines[idx].rate_pct` path: validateWith
// (lib/validation.js) flattens an issue by `path[0]` only, so a single-segment key is what
// lets a REPEATED row plug into the same fieldErrors map a flat form uses.
const TAX_CONFIG_SCHEMA = z.object({
  tax_lines: z.array(z.object({ rate_pct: z.any() })),
}).superRefine((val, ctx) => {
  (val.tax_lines || []).forEach((line, idx) => {
    const r = Number(line.rate_pct);
    if (!Number.isFinite(r) || r < 0 || r > 100) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [`rate_${idx}`], message: "tax.rateOutOfRange" });
    }
  });
});
// FIXED (B75): the preview used to have no currency to pass — the tax-preview payload
// carried none and `compute_checkout_breakdown` returned none, so `fmtMoney` silently
// fell back to CAD, wrong for any EUR/INR tenant on the one screen whose job is to say
// what they take home. `/tenant/checkout-breakdown` rows now include `currency`
// (lowercase ISO), so every `fmtMoney` call below passes `preview.currency` explicitly.

/**
 * Empty tax_profile shape (contract: GET/PUT /tenant/tax-config `tax_profile`).
 * `signature_name` has no editor in this iteration — it round-trips untouched via the
 * deep merge in `load()` rather than being clobbered by an empty default on save.
 */
const emptyTaxProfile = () => ({
  country: "",
  region: "",
  preset_id: "",
  legal_name: "",
  address: "",
  registration_numbers: { gst_hst: "", qst: "", pst: "", gstin: "", sales_permit: "" },
  india: { supplier_state_code: "", signature_name: "" },
});

// $100.00 — the amount the preview has always used; kept explicit so the copy and the
// query cannot drift apart.
const PREVIEW_CENTS = 10000;

/** The service type whose commission bites hardest, i.e. the honest default. */
const worstCase = (byType) =>
  Object.keys(byType).sort(
    (a, b) => (byType[b].commission_cents || 0) - (byType[a].commission_cents || 0)
      || a.localeCompare(b),
  )[0];

/**
 * TaxAdmin — Page tenant `/admin/tax-config`
 * Permet au praticien de définir ses taxes (TPS/TVQ, VAT, GST, HST…) qui s'appliquent à chaque checkout.
 */
export default function TaxAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  // QA2 H2-11 — the ONLY "Net for you" figure in the whole dashboard used to be pinned to
  // `service_type=course`, which carries a 0 % commission, on a page whose own header says
  // it governs "your courses, products AND consultations". A booking-only practitioner
  // read "commission 0 %, net 100 %" while every appointment was charged 10 %.
  //
  // The types come from `/public/stripe-connect/config.fees` — the same `_fee_labels()`
  // the Connect card reads, and a Mongo read with no Stripe round-trip — so this list
  // cannot drift from the backend's `CONNECT_FEE_SERVICE_TYPES` the way a hardcoded array
  // would. The DEFAULT is the type with the LARGEST commission, never the smallest: on a
  // page that answers "what do I keep", the safe default is the worst case. Picking the
  // best case is precisely the bug.
  const [previews, setPreviews] = useState(null);
  const [svcType, setSvcType] = useState(null);
  const preview = previews && svcType ? previews[svcType] : null;

  // B75 — jurisdiction presets (/public/tax-presets). `presets` stays `[]` on load
  // failure or while nothing has arrived yet, which is exactly the condition the
  // jurisdiction card below gates on: no presets → card absent, manual editor
  // untouched. This is a clean degrade, not an error state shown to the practitioner.
  const [presets, setPresets] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/tax-config");
      const base = emptyTaxProfile();
      const incoming = data?.tax_profile || {};
      setConfig({
        tax_lines: data?.tax_lines || [],
        tax_inclusive: !!data?.tax_inclusive,
        tax_profile: {
          ...base,
          ...incoming,
          registration_numbers: { ...base.registration_numbers, ...(incoming.registration_numbers || {}) },
          india: { ...base.india, ...(incoming.india || {}) },
        },
      });
    } catch (e) {
      toast.error(t("tax.loadError"));
    }
  }, [t]);

  const loadPresets = useCallback(async () => {
    try {
      const { data } = await api.get("/public/tax-presets");
      setPresets(data?.presets || []);
    } catch (e) {
      setPresets([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const previewBreakdown = useCallback(async () => {
    try {
      const { data: cfg } = await api.get("/public/stripe-connect/config");
      const types = Object.keys(cfg?.fees || {});
      if (!types.length) return;
      const rows = await Promise.all(types.map(async (st) => {
        const { data } = await api.get(`/tenant/checkout-breakdown?service_type=${encodeURIComponent(st)}&amount_cents=${PREVIEW_CENTS}`);
        return [st, data];
      }));
      const byType = Object.fromEntries(rows);
      setPreviews(byType);
      setSvcType((cur) => (cur && byType[cur] ? cur : worstCase(byType)));
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("[TaxAdmin] preview breakdown failed:", e?.message);
    }
  }, []);

  useEffect(() => { if (config) previewBreakdown(); }, [config, previewBreakdown]);

  if (!config) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-eh-muted" /></div>;

  const addLine = () => setConfig({ ...config, tax_lines: [...config.tax_lines, { name: "", rate_pct: 0, registration_number: "" }] });
  const updateLine = (idx, patch) => {
    const lines = [...config.tax_lines];
    lines[idx] = { ...lines[idx], ...patch };
    setConfig({ ...config, tax_lines: lines });
    if ("rate_pct" in patch) clearFieldError(`rate_${idx}`);
  };
  const removeLine = (idx) => setConfig({ ...config, tax_lines: config.tax_lines.filter((_, i) => i !== idx) });

  const updateProfile = (patch) => setConfig({ ...config, tax_profile: { ...config.tax_profile, ...patch } });
  const updateRegNumber = (key, val) => updateProfile({ registration_numbers: { ...config.tax_profile.registration_numbers, [key]: val } });
  const updateIndia = (key, val) => updateProfile({ india: { ...config.tax_profile.india, [key]: val } });

  // Unique countries across presets, in the order the backend listed them.
  const countries = [...new Set(presets.map((p) => p.country))];
  const regionsForCountry = presets.filter((p) => p.country === config.tax_profile.country);
  const selectedPreset = presets.find((p) => p.id === config.tax_profile.preset_id) || null;

  // The region <select>'s own option value: `region` when the preset has one
  // (CA provinces — unique within a country), else `id` (flat, country-level presets
  // like a second same-country tier, e.g. two India GST rates, would otherwise collide
  // on an empty region string).
  const regionKey = (p) => p.region || p.id;

  const onCountryChange = (country) => {
    updateProfile({ country, region: "", preset_id: "" });
  };

  const onRegionChange = (value) => {
    const preset = regionsForCountry.find((p) => regionKey(p) === value);
    if (!preset) return;
    const lines = preset.tax_lines || [];
    setConfig({
      ...config,
      tax_lines: lines.map((l) => ({ ...l, registration_number: "" })),
      tax_profile: { ...config.tax_profile, country: preset.country, region: preset.region, preset_id: preset.id },
    });
    toast.success(t("tax.presetApplied"));
  };

  const save = async () => {
    if (validate(TAX_CONFIG_SCHEMA, config)) { toast.error(tc("validation.fixHighlighted")); return; }
    setBusy(true);
    try {
      await api.put("/tenant/tax-config", config);
      toast.success(t("tax.saved"));
      previewBreakdown();
    } catch (e) {
      toast.error(extractApiError(e, t("tax.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="tax-admin" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <Receipt className="w-6 h-6 text-sol-gold" /> {t("tax.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">
          {t("tax.subtitle")}
        </p>
      </div>

      {presets.length > 0 && (
        <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-4" data-testid="tax-jurisdiction">
          <h3 className="font-display text-xl flex items-center gap-2">
            <Globe className="w-5 h-5 text-sol-gold" /> {t("tax.profile.title")}
          </h3>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-eh-muted">{t("tax.profile.country")}</label>
              <select
                value={config.tax_profile.country}
                onChange={(e) => onCountryChange(e.target.value)}
                data-testid="tax-country"
                className="w-full border rounded-md h-10 px-3 text-sm mt-1.5 bg-white"
              >
                <option value="">{t("tax.profile.custom")}</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-eh-muted">{t("tax.profile.region")}</label>
              <select
                value={selectedPreset ? regionKey(selectedPreset) : ""}
                onChange={(e) => onRegionChange(e.target.value)}
                data-testid="tax-region"
                className="w-full border rounded-md h-10 px-3 text-sm mt-1.5 bg-white"
              >
                <option value="">{t("tax.profile.regionPh")}</option>
                {regionsForCountry.map((p) => (
                  <option key={p.id} value={regionKey(p)}>{p.label?.[lang] || p.label?.en}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedPreset?.guidance && (
            <div className="bg-sol-gold/5 border border-sol-gold/20 rounded-lg p-3 text-sm text-eh-ink flex gap-2" data-testid="tax-guidance">
              <Info className="w-4 h-4 text-sol-gold shrink-0 mt-0.5" />
              <span>{selectedPreset.guidance[lang] || selectedPreset.guidance.en}</span>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-eh-muted">{t("tax.profile.legalName")}</label>
              <Input value={config.tax_profile.legal_name} onChange={(e) => updateProfile({ legal_name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <label className="text-xs text-eh-muted">{t("tax.profile.address")}</label>
              <Input value={config.tax_profile.address} onChange={(e) => updateProfile({ address: e.target.value })} className="mt-1.5" />
            </div>
          </div>

          {selectedPreset?.registration_fields?.length > 0 && (
            <div>
              <label className="text-xs text-eh-muted">{t("tax.profile.regNumbers")}</label>
              <div className="grid sm:grid-cols-2 gap-3 mt-1.5">
                {selectedPreset.registration_fields.map((f) => (
                  <Input
                    key={f.key}
                    value={config.tax_profile.registration_numbers[f.key] || ""}
                    onChange={(e) => updateRegNumber(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    data-testid={`tax-reg-${f.key}`}
                  />
                ))}
              </div>
            </div>
          )}

          {selectedPreset?.extra_fields?.map((f) => (
            f.type === "select" && f.key === "supplier_state_code" && (
              <div key={f.key}>
                <label className="text-xs text-eh-muted">{f.label?.[lang] || f.label?.en}</label>
                <select
                  value={config.tax_profile.india.supplier_state_code || ""}
                  onChange={(e) => updateIndia("supplier_state_code", e.target.value)}
                  data-testid="tax-supplier-state"
                  className="w-full border rounded-md h-10 px-3 text-sm mt-1.5 bg-white"
                >
                  <option value="" />
                  {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-eh-border-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">{t("tax.lines")}</h3>
          <Button size="sm" onClick={addLine} data-testid="add-tax" className="bg-eh-ink text-eh-bg">
            <Plus className="w-3 h-3 mr-1" /> {t("tax.add")}
          </Button>
        </div>

        {config.tax_lines.length === 0 ? (
          <p className="text-center text-eh-muted text-sm py-6">{t("tax.empty")}</p>
        ) : (
          <div className="space-y-2">
            {config.tax_lines.map((line, idx) => (
              <div key={idx} className="grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-center bg-eh-cream/20 rounded-lg p-3" data-testid={`tax-line-${idx}`}>
                <Input value={line.name} onChange={(e) => updateLine(idx, { name: e.target.value })} placeholder={t("tax.namePh")} data-testid={`tax-name-${idx}`} />
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.001"
                    value={line.rate_pct}
                    onChange={(e) => updateLine(idx, { rate_pct: parseFloat(e.target.value || "0") })}
                    placeholder={t("tax.ratePh")}
                    data-testid={`tax-rate-${idx}`}
                    className={`pr-7 ${fieldErrors[`rate_${idx}`] ? invalidInputClass : ""}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-eh-muted text-sm">%</span>
                  {/* `tax.rateOutOfRange` is an admin-namespace key — resolved through
                      this file's own `t`, not `tc` (which cannot see it). */}
                  <FieldError
                    message={fieldErrors[`rate_${idx}`] ? t(fieldErrors[`rate_${idx}`]) : ""}
                    testId={`tax-rate-${idx}-error`}
                  />
                </div>
                <Input value={line.registration_number || ""} onChange={(e) => updateLine(idx, { registration_number: e.target.value })} placeholder={t("tax.regPh")} />
                <button onClick={() => removeLine(idx)} className="p-2 text-rose-500 hover:bg-rose-50 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <label className="flex items-center gap-2 cursor-pointer text-sm mt-4">
          <input
            type="checkbox"
            checked={config.tax_inclusive}
            onChange={(e) => setConfig({ ...config, tax_inclusive: e.target.checked })}
            data-testid="tax-inclusive"
          />
          <span>{t("tax.inclusive")}</span>
        </label>

        <div className="mt-5">
          <Button onClick={save} disabled={busy} data-testid="tax-save" className="bg-eh-ink text-eh-bg">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} {t("tax.save")}
          </Button>
        </div>
      </div>

      {/* Live preview */}
      {preview && (
        <div className="bg-sol-gold/5 border border-sol-gold/20 rounded-2xl p-5" data-testid="tax-preview">
          <div className="flex items-center gap-2 mb-3 text-sm font-medium text-eh-ink">
            {/* NOT `tax.previewTitle` — that string reads "Preview on a $100.00 course"
                in all three locales, i.e. it hardcodes the very service type this fix
                removes from the query. */}
            <Info className="w-4 h-4 text-sol-gold" />{" "}
            {t("tax.previewTitleAny", { amount: fmtMoney(PREVIEW_CENTS, preview.currency) }, "Preview on a {amount} sale")}
          </div>
          {/* The type is CHOSEN, and visibly so: a single figure with no stated service
              type is what let a 0 %-commission preview pass for the practitioner's whole
              business. */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="tax-preview-types">
            {Object.keys(previews).map((st) => (
              <button
                key={st}
                onClick={() => setSvcType(st)}
                data-testid={`tax-preview-type-${st}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  svcType === st
                    ? "bg-eh-ink text-eh-bg border-eh-ink"
                    : "border-sol-gold/30 text-eh-muted hover:bg-sol-gold/10"
                }`}
              >
                {t(`sconn.svc.${st}`, {}, st)}
              </button>
            ))}
          </div>
          <div className="space-y-1.5 text-sm">
            <Row label={t("tax.subtotal")} value={fmtMoney(preview.subtotal_cents, preview.currency)} />
            {preview.tax_breakdown.map((tb) => (
              <Row key={tb.name} label={`${tb.name} (${tb.rate_pct}%)`} value={fmtMoney(tb.amount_cents, preview.currency)} subtle />
            ))}
            <Row label={t("tax.totalCharged")} value={fmtMoney(preview.total_charged_cents, preview.currency)} bold />
            <hr className="my-2 border-sol-gold/20" />
            <Row label={t("tax.commission", { pct: preview.fee_pct })} value={`-${fmtMoney(preview.commission_cents, preview.currency)}`} negative />
            <Row label={t("tax.net")} value={fmtMoney(preview.net_to_practitioner_cents, preview.currency)} bold accent />
          </div>
        </div>
      )}
    </div>
  );
}


function Row({ label, value, bold, subtle, accent, negative }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-medium" : ""} ${subtle ? "text-eh-muted text-xs pl-3" : ""}`}>
      <span>{label}</span>
      <span className={accent ? "text-emerald-700" : negative ? "text-rose-600" : "text-eh-ink"}>{value}</span>
    </div>
  );
}
